import React from 'react';
import { Bar, Line, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { parseYYYYMMDDAsLocal } from '../../utils/timezoneUtils';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

export default function SchoolAnalytics({ attendanceData, schoolData, dateRange, isViewingCustomReport, customReportDateRange }) {
  // Calculate school-level attendance statistics
  const calculateSchoolAnalytics = () => {
    console.log('SchoolAnalytics - attendanceData:', attendanceData);
    console.log('SchoolAnalytics - schoolData:', schoolData);
    console.log('SchoolAnalytics - dateRange:', dateRange);
    
    if (!attendanceData?.dailyAnalytics) {
      console.log('No daily analytics found in attendanceData');
      return null;
    }

    const dailyData = attendanceData.dailyAnalytics;
    
    // Debug: log the actual structure of daily data
    console.log('Daily data structure:', dailyData[0]);
    if (dailyData.length > 0) {
      console.log('Available properties:', Object.keys(dailyData[0]));
    }
    
    // Calculate overall school statistics
    const totalDays = dailyData.length;
    const averageAttendanceRate = totalDays > 0 
      ? dailyData.reduce((sum, day) => sum + parseFloat(day.attendanceRate || 0), 0) / totalDays 
      : 0;
    
    // Calculate total counts across all days
    // Use summaryTotals from backend if available (for correct unique student-day counts)
    // Otherwise fall back to summing daily counts (legacy support)
    console.log('📊 Debug - attendanceData.summaryTotals:', attendanceData.summaryTotals);
    console.log('📊 Debug - sample dailyData:', dailyData.slice(0, 3));
    
    const totalPresentCount = attendanceData.summaryTotals?.totalPresentStudentDays ?? 
      dailyData.reduce((sum, day) => sum + (day.presentCount || 0), 0);
    const totalLateCount = attendanceData.summaryTotals?.totalLateStudentDays ?? 
      dailyData.reduce((sum, day) => sum + (day.lateCount || 0), 0);
    const totalAbsentCount = attendanceData.summaryTotals?.totalAbsentStudentDays ?? 
      dailyData.reduce((sum, day) => sum + (day.absentCount || 0), 0);
    const totalExcusedCount = attendanceData.summaryTotals?.totalExcusedStudentDays ?? 
      dailyData.reduce((sum, day) => sum + (day.excusedCount || 0), 0);
    const totalRecords = attendanceData.summaryTotals?.totalStudentDayRecords ?? 
      dailyData.reduce((sum, day) => sum + (day.totalRecords || 0), 0);
    const totalUniqueStudents = Math.max(...dailyData.map(day => day.uniqueStudentCount || 0), 0);
    
    console.log('📊 Debug - calculated totals:', {
      totalPresentCount,
      totalAbsentCount,
      totalRecords,
      usingBackendSummary: !!attendanceData.summaryTotals
    });
    
    const result = {
      dailyData,
      totalDays,
      averageAttendanceRate,
      totalPresentCount,
      totalLateCount,
      totalAbsentCount,
      totalExcusedCount,
      totalRecords,
      totalUniqueStudents
    };

    console.log('School analytics result:', result);
    return result;
  };

  // Prepare chart data - bar chart for single day, line chart for multiple days
  const prepareChartData = () => {
    const analytics = calculateSchoolAnalytics();
    if (!analytics || !analytics.dailyData.length) return null;

    const isSingleDay = analytics.dailyData.length === 1;

    if (isSingleDay) {
      // Pie chart for single day showing Present vs Absent (Late is included in Present)
      const dayData = analytics.dailyData[0];
      
      const labels = ['Present', 'Absent'];
      const data = [
        dayData.presentCount || 0, // This already includes late students
        dayData.absentCount || 0
      ];
      
      const backgroundColors = [
        'rgba(34, 197, 94, 0.8)',   // Green for Present
        'rgba(239, 68, 68, 0.8)'    // Red for Absent
      ];
      
      const borderColors = [
        'rgba(34, 197, 94, 1)',
        'rgba(239, 68, 68, 1)'
      ];
      
      return {
        type: 'pie',
        labels,
        datasets: [
          {
            label: 'Number of Students',
            data,
            backgroundColor: backgroundColors,
            borderColor: borderColors,
            borderWidth: 2,
          }
        ],
        dayData // Store for tooltip access
      };
    } else {
      // Line chart for multiple days showing attendance trend
      const sortedDays = [...analytics.dailyData].sort((a, b) => {
        // For YYYY-MM-DD strings, simple string comparison works correctly
        return a.date.localeCompare(b.date);
      });
      
      const labels = sortedDays.map(day => {
        const date = parseYYYYMMDDAsLocal(day.date);
        if (!date) return day.date; // Fallback to original date string
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      });
      
      const studentsPresent = sortedDays.map(day => day.presentCount);
      
      return {
        type: 'line',
        labels,
        datasets: [
          {
            label: 'Students Present',
            data: studentsPresent,
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            borderColor: '#2563eb',
            borderWidth: 3,
            pointBackgroundColor: '#2563eb',
            pointBorderColor: '#ffffff',
            pointBorderWidth: 2,
            pointRadius: 6,
            pointHoverRadius: 8,
            fill: true,
            tension: 0.4
          }
        ],
        dailyData: sortedDays // Store for tooltip access
      };
    }
  };

  const chartData = prepareChartData();
  const schoolAnalytics = calculateSchoolAnalytics();
  const isSingleDay = schoolAnalytics?.dailyData?.length === 1;

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true, // Show legend for pie charts and multi-day line charts
        position: chartData?.type === 'pie' ? 'bottom' : 'top'
      },
      title: {
        display: true,
        text: (() => {
          if (isViewingCustomReport && customReportDateRange) {
            return isSingleDay 
              ? `${new Date(customReportDateRange.startDate).toLocaleDateString('en-US', { 
                  month: 'long', 
                  day: 'numeric', 
                  year: 'numeric' 
                })} - ${new Date(customReportDateRange.endDate).toLocaleDateString('en-US', { 
                  month: 'long', 
                  day: 'numeric', 
                  year: 'numeric' 
                })} Attendance Report - ${schoolData?.name || 'School'}`
              : `Daily Student Attendance Report - ${schoolData?.name || 'School'}`;
          }
          return isSingleDay 
            ? `Today's Attendance Overview - ${schoolData?.name || 'School'}`
            : `Daily Student Attendance - ${schoolData?.name || 'School'}`;
        })(),
        font: {
          size: 18,
          weight: 'bold'
        },
        color: '#1e293b',
        padding: 20
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: 'rgba(255, 255, 255, 0.1)',
        borderWidth: 1,
        callbacks: {
          label: function(context) {
            if (chartData?.type === 'pie') {
              // Pie chart tooltip
              const value = context.parsed;
              const total = context.dataset.data.reduce((a, b) => a + b, 0);
              const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
              return `${context.label}: ${value} students (${percentage}%)`;
            } else if (isSingleDay) {
              // Single day bar chart tooltip (if we still have any)
              const value = context.parsed.y;
              const total = chartData?.dayData ? 
                (chartData.dayData.presentCount || 0) + (chartData.dayData.absentCount || 0) : 
                context.dataset.data.reduce((a, b) => a + b, 0);
              const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
              return `${context.label}: ${value} students (${percentage}%)`;
            } else {
              // Multi-day line chart tooltip
              const dataIndex = context.dataIndex;
              const dayData = chartData.dailyData[dataIndex];
              const studentsPresent = context.parsed.y;
              const totalStudents = dayData.totalRecords;
              const attendanceRate = totalStudents > 0 ? ((studentsPresent / totalStudents) * 100) : 0;
              
              return [
                `Students Present: ${studentsPresent}`,
                `Total Students: ${totalStudents}`,
                `Attendance Rate: ${attendanceRate.toFixed(1)}%`,
                `Date: ${dayData.date}`
              ];
            }
          }
        }
      }
    },
    // Only include scales for bar and line charts, not pie charts
    ...(chartData?.type !== 'pie' && {
      scales: {
        y: {
          beginAtZero: true,
          title: {
            display: true,
            text: isSingleDay ? 'Number of Students' : 'Number of Students Present',
            font: {
              size: 14,
              weight: 'bold'
            }
          },
          ticks: {
            stepSize: 1,
            callback: function(value) {
              return Math.floor(value); // Ensure whole numbers only
            }
          },
          grid: {
            color: 'rgba(0, 0, 0, 0.1)'
          }
        },
        x: {
          title: {
            display: true,
            text: isSingleDay ? 'Attendance Status' : 'Date',
            font: {
              size: 14,
              weight: 'bold'
            }
          },
          grid: {
            display: !isSingleDay // Hide grid for bar chart, show for line chart
          },
          ticks: isSingleDay ? {} : {
            maxRotation: 45,
            minRotation: 45
          }
        }
      }
    }),
    animation: {
      duration: 1000,
      easing: 'easeOutQuart'
    }
  };

  if (!attendanceData || !schoolData) {
    return (
      <div className="text-center py-16">
        <div className="max-w-md mx-auto">
          <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl">📊</span>
          </div>
          <h3 className="text-xl font-semibold text-slate-800 mb-2">School Analytics</h3>
          <p className="text-slate-600 mb-6">
            Select a school and ensure attendance data is loaded to view school-level analytics.
          </p>
        </div>
      </div>
    );
  }

  // Check if we have no attendance records for the selected school
  if (!schoolAnalytics || (attendanceData?.dailyAnalytics && attendanceData.dailyAnalytics.length === 0)) {
    return (
      <div className="text-center py-16">
        <div className="max-w-md mx-auto">
          <h3 className="text-xl font-semibold text-slate-800 mb-2">No Reports Found</h3>
          <p className="text-slate-600 mb-2">
            No attendance records found for {schoolData.name} in the selected date range.
          </p>
          <p className="text-sm text-slate-500 mt-4">
            Try selecting a different date range to view attendance data.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center mb-2">
        <p className="text-slate-600">
          {isViewingCustomReport && customReportDateRange ? (
            <>
              {`${new Date(customReportDateRange.startDate).toLocaleDateString('en-US', { 
                month: 'long', 
                day: 'numeric', 
                year: 'numeric' 
              })} - ${new Date(customReportDateRange.endDate).toLocaleDateString('en-US', { 
                month: 'long', 
                day: 'numeric', 
                year: 'numeric' 
              })} Report for `}<span className="font-semibold text-blue-600">{schoolData.name}</span>
            </>
          ) : (
            <>
              Today's attendance overview for <span className="font-semibold text-blue-600">{schoolData.name}</span>
            </>
          )}
        </p>
        {/*<p className="text-sm text-slate-500 mt-1">
          Date range: {dateRange?.startDate} to {dateRange?.endDate}
        </p>*/}
      </div>

      {/* Analytics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
        {/* Total Days Card */}
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200 p-6 shadow-sm hover:shadow-lg transition-all duration-200">
          <div className="flex items-center justify-between mb-4">
            <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-blue-600">
                {schoolAnalytics.totalDays}
              </div>
              <div className="text-sm text-blue-500 font-medium">
                school days
              </div>
            </div>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-blue-800 mb-1">Total Days</h3>
            <p className="text-xs text-blue-600">
              Days with attendance records in the selected date range
            </p>
          </div>
        </div>

        {/* Average Attendance Card */}
        <div className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-xl border border-purple-200 p-6 shadow-sm hover:shadow-lg transition-all duration-200">
          <div className="flex items-center justify-between mb-4">
            <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v4a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-purple-600">
                {schoolAnalytics.averageAttendanceRate.toFixed(1)}%
              </div>
              <div className="text-sm text-purple-500 font-medium">
                average rate
              </div>
            </div>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-purple-800 mb-1">Daily Average</h3>
            <p className="text-xs text-purple-600">
              Average attendance rate across all school days
            </p>
            <div className="mt-2">
              <div className="w-full bg-purple-100 rounded-full h-2">
                <div 
                  className="bg-purple-500 h-2 rounded-full transition-all duration-500" 
                  style={{ width: `${Math.min(100, schoolAnalytics.averageAttendanceRate)}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>

        {/* Total Students Card */}
        <div className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-xl border border-indigo-200 p-6 shadow-sm hover:shadow-lg transition-all duration-200">
          <div className="flex items-center justify-between mb-4">
            <div className="w-10 h-10 bg-indigo-500 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.196-2.121M17 20v-2a3 3 0 00-3-3h-4a3 3 0 00-3 3v2m4-8a3 3 0 110-6 3 3 0 010 6z" />
              </svg>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-indigo-600">
                {schoolAnalytics.totalUniqueStudents}
              </div>
              <div className="text-sm text-indigo-500 font-medium">
                unique students
              </div>
            </div>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-indigo-800 mb-1">Total Students</h3>
            <p className="text-xs text-indigo-600">
              Maximum unique students with attendance in any single day
            </p>
          </div>
        </div>

        {/* Total Records Card */}
        <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl border border-emerald-200 p-6 shadow-sm hover:shadow-lg transition-all duration-200">
          <div className="flex items-center justify-between mb-4">
            <div className="w-10 h-10 bg-emerald-500 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
              </svg>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-emerald-600">
                {schoolAnalytics.totalRecords}
              </div>
              <div className="text-sm text-emerald-500 font-medium">
                total records
              </div>
            </div>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-emerald-800 mb-1">Total Records</h3>
            <p className="text-xs text-emerald-600">
              Total student attendance records across all days
            </p>
            <div className="mt-2 text-xs text-emerald-700">
              Present: {schoolAnalytics.totalPresentCount}, Absent: {schoolAnalytics.totalAbsentCount}
            </div>
          </div>
        </div>
      </div>

      {/* Attendance Chart - Pie for single day, Line for multiple days */}
      {chartData && (
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
          <div className="h-[500px]">
            {chartData.type === 'pie' ? (
              <Pie data={chartData} options={chartOptions} />
            ) : chartData.type === 'bar' ? (
              <Bar data={chartData} options={chartOptions} />
            ) : (
              <Line data={chartData} options={chartOptions} />
            )}
          </div>
          {isSingleDay ? (
            // Single day legend (Present/Absent only)
            <div className="mt-6 text-center">
              <div className="inline-flex items-center space-x-6 text-sm">
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-green-500 rounded mr-2"></div>
                  <span className="text-slate-600">Present: {chartData.dayData?.presentCount || 0}</span>
                </div>
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-red-500 rounded mr-2"></div>
                  <span className="text-slate-600">Absent: {chartData.dayData?.absentCount || 0}</span>
                </div>
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-slate-400 rounded mr-2"></div>
                  <span className="text-slate-600">Total: {(chartData.dayData?.presentCount || 0) + (chartData.dayData?.absentCount || 0)}</span>
                </div>
              </div>
            </div>
          ) : (
            // Multi-day explanation
            <div className="mt-4 text-sm text-slate-600">
              <p>Daily student attendance showing the total number of students present (including late arrivals) for each day.</p>
              <p>Hover over points to see detailed breakdown for each day.</p>
            </div>
          )}
        </div>
      )}

      {/* Summary Section - different layout for single vs multi-day */}
      {/*schoolAnalytics && schoolAnalytics.dailyData.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
          {isSingleDay ? (
            // Single day summary
            <>
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Today's Summary</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Date:</span>
                    <span className="font-medium text-slate-900">
                      {(() => {
                        const date = parseYYYYMMDDAsLocal(schoolAnalytics.dailyData[0].date);
                        return date ? date.toLocaleDateString('en-US', { 
                          weekday: 'long', 
                          month: 'long', 
                          day: 'numeric',
                          year: 'numeric'
                        }) : schoolAnalytics.dailyData[0].date;
                      })()}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Total Students:</span>
                    <span className="font-medium text-slate-900">{schoolAnalytics.dailyData[0].uniqueStudentCount}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Students Present:</span>
                    <span className="font-medium text-green-600">{schoolAnalytics.dailyData[0].presentCount}</span>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Students Absent:</span>
                    <span className="font-medium text-red-600">{schoolAnalytics.dailyData[0].absentCount}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Total Records:</span>
                    <span className="font-medium text-slate-900">{schoolAnalytics.dailyData[0].totalRecords}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Attendance Rate:</span>
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                      parseFloat(schoolAnalytics.dailyData[0].attendanceRate) >= 95 ? 'bg-green-100 text-green-800' :
                      parseFloat(schoolAnalytics.dailyData[0].attendanceRate) >= 85 ? 'bg-lime-100 text-lime-800' :
                      parseFloat(schoolAnalytics.dailyData[0].attendanceRate) >= 70 ? 'bg-yellow-100 text-yellow-800' :
                      parseFloat(schoolAnalytics.dailyData[0].attendanceRate) >= 50 ? 'bg-orange-100 text-orange-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {parseFloat(schoolAnalytics.dailyData[0].attendanceRate || 0).toFixed(1)}%
                    </span>
                  </div>
                </div>
              </div>
            </>
          ) : (
            // Multi-day breakdown table
            <>
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Daily Breakdown</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 font-semibold text-slate-700">Date</th>
                      <th className="text-center py-3 px-4 font-semibold text-slate-700">Unique Students</th>
                      <th className="text-center py-3 px-4 font-semibold text-slate-700">Present</th>
                      <th className="text-center py-3 px-4 font-semibold text-slate-700">Late</th>
                      <th className="text-center py-3 px-4 font-semibold text-slate-700">Absent</th>
                      <th className="text-center py-3 px-4 font-semibold text-slate-700">Total Records</th>
                      <th className="text-center py-3 px-4 font-semibold text-slate-700">Attendance Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {schoolAnalytics.dailyData
                      .sort((a, b) => b.date.localeCompare(a.date)) // Most recent first (YYYY-MM-DD string comparison)
                      .map((day) => (
                        <tr key={day.date} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="py-3 px-4 font-medium text-slate-900">
                            {(() => {
                              const date = parseYYYYMMDDAsLocal(day.date);
                              return date ? date.toLocaleDateString('en-US', { 
                                weekday: 'short', 
                                month: 'short', 
                                day: 'numeric' 
                              }) : day.date;
                            })()}
                          </td>
                          <td className="text-center py-3 px-4 text-slate-600">{day.uniqueStudentCount}</td>
                          <td className="text-center py-3 px-4 text-green-600 font-medium">{day.presentCount}</td>
                          <td className="text-center py-3 px-4 text-yellow-600 font-medium">{day.lateCount}</td>
                          <td className="text-center py-3 px-4 text-red-600 font-medium">{day.absentCount}</td>
                          <td className="text-center py-3 px-4 text-slate-600">{day.totalRecords}</td>
                          <td className="text-center py-3 px-4">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              parseFloat(day.attendanceRate) >= 95 ? 'bg-green-100 text-green-800' :
                              parseFloat(day.attendanceRate) >= 85 ? 'bg-lime-100 text-lime-800' :
                              parseFloat(day.attendanceRate) >= 70 ? 'bg-yellow-100 text-yellow-800' :
                              parseFloat(day.attendanceRate) >= 50 ? 'bg-orange-100 text-orange-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {parseFloat(day.attendanceRate || 0).toFixed(1)}%
                            </span>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )*/}

      {/* Class Statistics Table */}
      {attendanceData?.classStatistics && attendanceData.classStatistics.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm mt-6">
          <div className="px-6 py-4 border-b border-slate-200">
            <h3 className="text-lg font-semibold text-slate-800">Individual Class Statistics</h3>
            <p className="text-sm text-slate-600 mt-1">
              Detailed attendance breakdown for each class during the report period
            </p>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left py-3 px-4 font-semibold text-slate-700">Class</th>
                  <th className="text-left py-3 px-4 font-semibold text-slate-700">Subject</th>
                  <th className="text-center py-3 px-4 font-semibold text-slate-700">Sessions</th>
                  <th className="text-center py-3 px-4 font-semibold text-slate-700">Students</th>
                  <th className="text-center py-3 px-4 font-semibold text-slate-700">Records</th>
                  <th className="text-center py-3 px-4 font-semibold text-slate-700">Present</th>
                  <th className="text-center py-3 px-4 font-semibold text-slate-700">Late</th>
                  <th className="text-center py-3 px-4 font-semibold text-slate-700">Absent</th>
                  <th className="text-center py-3 px-4 font-semibold text-slate-700">Excused</th>
                  <th className="text-center py-3 px-4 font-semibold text-slate-700">Attendance Rate</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {attendanceData.classStatistics.map((classData, index) => (
                  <tr key={`${classData.classId}-${index}`} className="hover:bg-slate-50">
                    <td className="py-3 px-4 font-medium text-slate-900">
                      {classData.className}
                    </td>
                    <td className="py-3 px-4 text-slate-600">
                      {classData.subjectName}
                    </td>
                    <td className="text-center py-3 px-4 text-slate-600">
                      {classData.totalSessions}
                    </td>
                    <td className="text-center py-3 px-4 text-slate-600">
                      {classData.uniqueStudents}
                    </td>
                    <td className="text-center py-3 px-4 text-slate-600">
                      {classData.totalRecords}
                    </td>
                    <td className="text-center py-3 px-4 text-green-600 font-medium">
                      {classData.presentCount}
                    </td>
                    <td className="text-center py-3 px-4 text-yellow-600 font-medium">
                      {classData.lateCount}
                    </td>
                    <td className="text-center py-3 px-4 text-red-600 font-medium">
                      {classData.absentCount}
                    </td>
                    <td className="text-center py-3 px-4 text-blue-600 font-medium">
                      {classData.excusedCount}
                    </td>
                    <td className="text-center py-3 px-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        parseFloat(classData.attendanceRate) >= 95 ? 'bg-green-100 text-green-800' :
                        parseFloat(classData.attendanceRate) >= 85 ? 'bg-lime-100 text-lime-800' :
                        parseFloat(classData.attendanceRate) >= 70 ? 'bg-yellow-100 text-yellow-800' :
                        parseFloat(classData.attendanceRate) >= 50 ? 'bg-orange-100 text-orange-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {parseFloat(classData.attendanceRate || 0).toFixed(1)}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Period Breakdown for Classes */}
          {attendanceData.classStatistics.some(classData => classData.periodBreakdown && classData.periodBreakdown.length > 0) && (
            <div className="px-6 py-4 border-t border-slate-200">
              <h4 className="text-md font-semibold text-slate-800 mb-4">Period Breakdown by Class</h4>
              <div className="space-y-6">
                {attendanceData.classStatistics
                  .filter(classData => classData.periodBreakdown && classData.periodBreakdown.length > 0)
                  .map((classData, classIndex) => (
                    <div key={`period-${classData.classId}-${classIndex}`} className="bg-slate-50 rounded-lg p-4">
                      <h5 className="text-sm font-semibold text-slate-700 mb-3">
                        {classData.className} - {classData.subjectName}
                      </h5>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-slate-300">
                              <th className="text-left py-2 px-3 font-medium text-slate-600">Period</th>
                              <th className="text-center py-2 px-3 font-medium text-slate-600">Sessions</th>
                              <th className="text-center py-2 px-3 font-medium text-slate-600">Students</th>
                              <th className="text-center py-2 px-3 font-medium text-slate-600">Records</th>
                              <th className="text-center py-2 px-3 font-medium text-slate-600">Present</th>
                              <th className="text-center py-2 px-3 font-medium text-slate-600">Late</th>
                              <th className="text-center py-2 px-3 font-medium text-slate-600">Absent</th>
                              <th className="text-center py-2 px-3 font-medium text-slate-600">Rate</th>
                            </tr>
                          </thead>
                          <tbody>
                            {classData.periodBreakdown.map((period, periodIndex) => (
                              <tr key={`${classData.classId}-${period.periodName}-${periodIndex}`}>
                                <td className="py-2 px-3 text-slate-700">{period.periodName}</td>
                                <td className="text-center py-2 px-3 text-slate-600">{period.sessions}</td>
                                <td className="text-center py-2 px-3 text-slate-600">{period.uniqueStudents}</td>
                                <td className="text-center py-2 px-3 text-slate-600">{period.totalRecords}</td>
                                <td className="text-center py-2 px-3 text-green-600">{period.presentCount}</td>
                                <td className="text-center py-2 px-3 text-yellow-600">{period.lateCount}</td>
                                <td className="text-center py-2 px-3 text-red-600">{period.absentCount}</td>
                                <td className="text-center py-2 px-3">
                                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                    parseFloat(period.attendanceRate) >= 90 ? 'bg-green-100 text-green-700' :
                                    parseFloat(period.attendanceRate) >= 75 ? 'bg-yellow-100 text-yellow-700' :
                                    'bg-red-100 text-red-700'
                                  }`}>
                                    {parseFloat(period.attendanceRate || 0).toFixed(1)}%
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
